// src/components/Header.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

// Liens externes par défaut
const KRAC_URL_DEFAULT = 'https://kracradio.com';
const DONATE_URL_DEFAULT = 'https://www.paypal.com/donate/?hosted_button_id=GUPL4K5WR3ZG4';
const STORE_URL_DEFAULT = 'https://store.kracradio.com';

// Style: pills permanents + léger contraste
const navPill =
  "px-3 py-2 text-sm rounded-lg border transition-colors " +
  "bg-gray-100 text-gray-900 border-gray-200 " +
  "hover:bg-gray-200 hover:border-gray-300 hover:text-black " +
  "dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 " +
  "dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:hover:text-white";

const btnRed =
  "px-3 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors";

const btnAmber =
  "px-3 py-2 rounded-lg text-sm font-medium bg-amber-500 text-black hover:bg-amber-600 transition-colors";

const btnTheme =
  "rounded-lg font-medium transition-colors flex items-center justify-center " +
  "bg-gray-100 hover:bg-gray-200 text-gray-800 " +
  "dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-100";

const Header = ({
  logoSrc = '/logo.png',
  homeHref = '/mp3tagedit',
  aboutHref = '/about',
  historyHref = '/history',
  contactHref = '/contact',
  kracUrl = KRAC_URL_DEFAULT,
  donateUrl = DONATE_URL_DEFAULT,
  storeUrl = STORE_URL_DEFAULT,
}) => {
  const { t, language, setLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);

  const openInNewTab = (url) => {
    try { window.open(url, '_blank', 'noopener,noreferrer'); }
    catch { window.location.href = url; }
  };

  const L = {
    home: t?.header?.home ?? 'Accueil',
    about: t?.header?.about ?? 'À propos',
    history: t?.header?.history ?? 'Historique',
    contact: t?.header?.contact ?? 'Contact',
    kracRadio: t?.header?.kracRadio ?? 'KracRadio',
    donation: t?.header?.donation ?? 'Faire un don',
    store: t?.header?.store ?? 'Boutique',
  };

  return (
    <header className="border-b bg-white text-gray-900 border-gray-200 dark:bg-gray-900 dark:text-white dark:border-gray-800">

      {/* ====== BARRE MOBILE (md:hidden) ====== */}
      <div className="md:hidden flex items-center justify-between h-16">
        {/* Gauche: logo (pl-5 = 20px) */}
        <div className="pl-5">
          <Link to={homeHref} aria-label="Accueil">
            <img src={logoSrc} alt="Logo" className="h-8 w-auto rounded-md" />
          </Link>
        </div>

        {/* Droite: thème + langue + hamburger (pr-5 = 20px) */}
        <div className="pr-5 flex items-center gap-2">
          {/* Switch thème — touch target 44px */}
          <button
            type="button"
            onClick={toggleTheme}
            title={isDark ? 'Light' : 'Dark'}
            aria-label="Basculer le thème"
            className={`${btnTheme} w-11 h-11 rounded-xl`}
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>

          {/* Sélecteur langue — touch-friendly */}
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            title="Language"
            className="h-11 px-3 text-sm rounded-xl bg-white border border-gray-300 text-gray-700
                       dark:bg-gray-900 dark:border-gray-700 dark:text-gray-200"
          >
            <option className="text-black" value="fr">FR</option>
            <option className="text-black" value="en">EN</option>
            <option className="text-black" value="es">ES</option>
          </select>

          <button
            type="button"
            onClick={() => openInNewTab(storeUrl)}
            className="px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          >
            {L.store}
          </button>

          {/* Hamburger — touch target 44px */}
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            aria-label="Ouvrir le menu"
            className="w-11 h-11 rounded-xl flex items-center justify-center
                       hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Menu mobile déroulant */}
      {open && (
        <div className="md:hidden border-t bg-white border-gray-200 dark:bg-gray-900 dark:border-gray-800">
          <div className="px-4 py-3 flex flex-col gap-2">
            <Link to={homeHref} onClick={() => setOpen(false)} className={navPill}>
              {L.home}
            </Link>
            <Link to={aboutHref} onClick={() => setOpen(false)} className={navPill}>
              {L.about}
            </Link>
            <Link to={historyHref} onClick={() => setOpen(false)} className={navPill}>
              {L.history}
            </Link>
            <Link to={contactHref} onClick={() => setOpen(false)} className={navPill}>
              {L.contact}
            </Link>

            <div className="flex items-center gap-3 pt-2">
              <button type="button" onClick={() => { setOpen(false); openInNewTab(storeUrl); }} className="px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors">
                {L.store}
              </button>

              <button type="button" onClick={() => { setOpen(false); openInNewTab(kracUrl); }} className={btnRed}>
                {L.kracRadio}
              </button>

              <button type="button" onClick={() => { setOpen(false); openInNewTab(donateUrl); }} className={btnAmber}>
                {L.donation}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== BARRE DESKTOP (≥ md) : inchangée ====== */}
      <div className="hidden md:grid w-full grid-cols-[1fr_auto_1fr] items-center h-16">
        {/* gauche: logo + 50px */}
        <div className="justify-self-start pl-[50px] flex items-center gap-3">
          <Link to={homeHref}>
            <img src={logoSrc} alt="Logo" className="h-8 w-auto rounded-md" />
          </Link>
          <span className="text-lg font-semibold hidden lg:block">MP3 TAG ID3</span>
        </div>

        {/* centre: menu */}
        <nav className="hidden md:flex justify-center">
          <ul className="flex items-center gap-2">
            <li><Link to={homeHref} className={navPill}>{L.home}</Link></li>
            <li><Link to={aboutHref} className={navPill}>{L.about}</Link></li>
            <li><Link to={historyHref} className={navPill}>{L.history}</Link></li>
            <li><Link to={contactHref} className={navPill}>{L.contact}</Link></li>
          </ul>
        </nav>

        {/* droite: langue + CTA + thème (+50px) */}
        <div className="justify-self-end pr-[50px] hidden md:flex items-center gap-3">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            title="Language"
            className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg px-2 py-1
                       dark:bg-gray-900 dark:border-gray-700 dark:text-gray-200"
          >
            <option className="text-black" value="fr">FR</option>
            <option className="text-black" value="en">EN</option>
            <option className="text-black" value="es">ES</option>
          </select>

          <button type="button" onClick={() => openInNewTab(storeUrl)} className="px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors">
            {L.store}
          </button>

          <button type="button" onClick={() => openInNewTab(kracUrl)} className={btnRed}>
            {L.kracRadio}
          </button>

          <button type="button" onClick={() => openInNewTab(donateUrl)} className={btnAmber}>
            {L.donation}
          </button>

          <button
            type="button"
            onClick={toggleTheme}
            title={isDark ? 'Light' : 'Dark'}
            aria-label="Basculer le thème"
            className={`${btnTheme} px-3 py-2 rounded-lg`}
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            <span className="hidden lg:inline">{isDark ? 'Light' : 'Dark'}</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
